import { useCallback, useEffect, useState } from "react";
import { oauthHealthService, PluginConnectionStatus } from "../services/OAuthHealthService";

export interface UseOAuthHealthReturn {
  isPluginHealthy: (pluginType: string) => Promise<boolean>;
  getPluginStatus: (pluginType: string) => Promise<PluginConnectionStatus | null>;
  clearCache: () => void;
}

/**
 * Hook to manage OAuth health checks for plugins
 */
export const useOAuthHealth = (): UseOAuthHealthReturn => {
  const isPluginHealthy = useCallback(async (pluginType: string): Promise<boolean> => {
    try {
      return await oauthHealthService.isPluginHealthy(pluginType);
    } catch (error) {
      console.error(`useOAuthHealth: Failed to check ${pluginType} health:`, error);
      return false;
    }
  }, []);

  const getPluginStatus = useCallback(async (pluginType: string): Promise<PluginConnectionStatus | null> => {
    try {
      return await oauthHealthService.checkPluginConnection(pluginType);
    } catch (error) {
      console.error(`useOAuthHealth: Failed to get ${pluginType} status:`, error);
      return null;
    }
  }, []);

  const clearCache = useCallback(() => {
    oauthHealthService.clearCache();
  }, []);

  return {
    isPluginHealthy,
    getPluginStatus,
    clearCache,
  };
};

/**
 * Hook to check OAuth health for a specific plugin
 */
export const usePluginHealth = (pluginType: string) => {
  const [connectionStatus, setConnectionStatus] = useState<PluginConnectionStatus | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const checkConnection = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const status = await oauthHealthService.checkPluginConnection(pluginType);
      setConnectionStatus(status);
    } catch (err) {
      console.error(`usePluginHealth: Failed to check ${pluginType} connection:`, err);
      setError(err instanceof Error ? err.message : `Failed to check ${pluginType} connection`);
    } finally {
      setIsLoading(false);
    }
  }, [pluginType]);

  useEffect(() => {
    checkConnection();
  }, [checkConnection]);

  return {
    connectionStatus,
    isLoading,
    error,
    checkConnection,
    isHealthy: connectionStatus ? connectionStatus.connected && !connectionStatus.requiresReauth && connectionStatus.hasValidTokens : false,
    requiresReauth: connectionStatus ? connectionStatus.requiresReauth : false,
  };
};
